import { createSlice } from '@reduxjs/toolkit';
import { DealObject } from '../../params/pipeline';
import { closeAndUnCloseDealThunk, createDealFileThunk, createDealThunk, dealCustomFieldsUpdateThunk, deleteDealFileThunk, editDealFileThunk, editDealThunk, fetchAllDealFilesThunk, fetchAllDealsThunk, fetchDealDetailThunk, getArchievedDealsThunk, restoreDealThunk, transferDealThunk, updateDealOrderThunk, updateDealStatusThunk } from '../thunks/deal';
import { DealFileObject, DealState } from '../../params/redux/deal';

// Initial state with default values for authentication and user info
const initialState: DealState = {
  deals: [],
  selectedDeal: null,
  selectedClientsDeal: null,
  selectedDealDetail: null,
  archievedDeals: [],
  selectedDealFiles: {
    files: [],
    selectedFile: null
  },
  updateDealValues: {
    firstStep: null,
    secondStep: null
  },
  selectedTab: 1,
  flags: {
    showOverviewModal: 0,
    viewDetail: false,
    isAddingDeal: false,
    editDeal: false,
    showArchievedList: false,
    showEmailAndSMS: null
  },
  loaders: {
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isSearching: false,
    isInserting: false
  }
};

// Slice for app-related states and actions
const dealSlice = createSlice({
  // Slice name
  name: 'deal',
  // inital state for the slice
  initialState,
  reducers: {
    setAddingDeal: (state, action) => {
      state.flags.isAddingDeal = action.payload;
    },
    setFirstStepDetailsOfDeal: (state, action) => {
      state.updateDealValues.firstStep = action.payload;
    },
    setSecondStepDetailsOfDeal: (state, action) => {
      state.updateDealValues.secondStep = action.payload;
    },
    setInitialDealStepValues: (state) => {
      state.updateDealValues = {
        firstStep: null,
        secondStep: null
      };
    },
    settingSelectedEmailAndSMSTab: (state, action) => {
      state.flags.showEmailAndSMS = action.payload;
    },
    setEditDeal: (state, action) => {
      state.flags.editDeal = action.payload;
    },
    settingSelectedDealFile: (state, action) => {
      state.selectedDealFiles.selectedFile = action.payload;
    },
    settingSelectedDeal: (state, action) => {
      state.selectedDeal = action.payload;
    },
    settingSelectedClientDeal: (state, action) => {
      state.selectedClientsDeal = action.payload;
    },
    settingSelectedDealTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    settingOverviewModal: (state, action) => {
      state.flags.showOverviewModal = action.payload;
    },
    settingArhievedList: (state, action) => {
      state.flags.showArchievedList = action.payload;
    },
    settingViewDetail: (state, action) => {
      state.flags.viewDetail = action.payload;
    },
    // Resets loading states related to app actions
    resettingDealLoader: (state) => {
      state.loaders = initialState.loaders;
    },
    // Resets all app data to the initial state
    resettingDealData: () => initialState
  },
  extraReducers: (builder) => {
    // Handle async actions for login
    builder
      .addCase(fetchDealDetailThunk.pending, (state) => {
        // Set loading flag during login request
        state.loaders.isLoading = true;
      })
      .addCase(fetchDealDetailThunk.fulfilled, (state, action) => {
        state.selectedDealDetail = action.payload as DealObject;
      })
      .addCase(fetchDealDetailThunk.rejected, () => {
        //
      });
    builder
      .addCase(createDealThunk.pending, (state) => {
        state.loaders.isCreating = true;
      })
      .addCase(createDealThunk.fulfilled, () => {
        //
      })
      .addCase(createDealThunk.rejected, () => {
        //
      });
    builder
      .addCase(updateDealOrderThunk.pending, () => {
        //
      })
      .addCase(updateDealOrderThunk.fulfilled, () => {
        //
      })
      .addCase(updateDealOrderThunk.rejected, () => {
        //
      });
    builder
      .addCase(transferDealThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(transferDealThunk.fulfilled, () => {
        //
      })
      .addCase(transferDealThunk.rejected, () => {
        //
      });
    builder
      .addCase(updateDealStatusThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(updateDealStatusThunk.fulfilled, () => {
        //
      })
      .addCase(updateDealStatusThunk.rejected, () => {
        //
      });
    builder
      .addCase(getArchievedDealsThunk.pending, (state) => {
        state.loaders.isLoading = true;
      })
      .addCase(getArchievedDealsThunk.fulfilled, (state, action) => {
        state.archievedDeals = action.payload as [];
      })
      .addCase(getArchievedDealsThunk.rejected, (state) => {
        state.archievedDeals = [];
      });
    builder
      .addCase(dealCustomFieldsUpdateThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(dealCustomFieldsUpdateThunk.fulfilled, () => {
        //
      })
      .addCase(dealCustomFieldsUpdateThunk.rejected, () => {
        //
      });
    builder
      .addCase(restoreDealThunk.pending, (state) => {
        state.loaders.isLoading = true;
      })
      .addCase(restoreDealThunk.fulfilled, () => {
        //
      })
      .addCase(restoreDealThunk.rejected, () => {
        //
      });
    builder
      .addCase(editDealThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(editDealThunk.fulfilled, () => {
        //
      })
      .addCase(editDealThunk.rejected, () => {
        //
      });
    builder
      .addCase(closeAndUnCloseDealThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(closeAndUnCloseDealThunk.fulfilled, () => {
        //
      })
      .addCase(closeAndUnCloseDealThunk.rejected, () => {
        //
      });
    builder
      .addCase(fetchAllDealsThunk.pending, (state) => {
        state.loaders.isLoading = true;
      })
      .addCase(fetchAllDealsThunk.fulfilled, (state, action) => {
        state.deals = action.payload as DealObject[];
      })
      .addCase(fetchAllDealsThunk.rejected, () => {
        //
      });
      builder
      .addCase(fetchAllDealFilesThunk.pending, (state) => {
        state.loaders.isLoading = true;
      })
      .addCase(fetchAllDealFilesThunk.fulfilled, (state, action) => {
        state.selectedDealFiles.files = action.payload as DealFileObject[];
      })
      .addCase(fetchAllDealFilesThunk.rejected, (state) => {
        state.selectedDealFiles.files = [];
      });
      builder
      .addCase(createDealFileThunk.pending, (state) => {
        state.loaders.isCreating = true;
      })
      .addCase(createDealFileThunk.fulfilled, () => {
        //
      })
      .addCase(createDealFileThunk.rejected, () => {
        //
      });
      builder
      .addCase(editDealFileThunk.pending, (state) => {
        state.loaders.isUpdating = true;
      })
      .addCase(editDealFileThunk.fulfilled, () => {
        //
      })
      .addCase(editDealFileThunk.rejected, () => {
        //
      });
      builder
      .addCase(deleteDealFileThunk.pending, (state) => {
        state.loaders.isDeleting = true;
      })
      .addCase(deleteDealFileThunk.fulfilled, () => {
        //
      })
      .addCase(deleteDealFileThunk.rejected, () => {
        //
      });
  }
});

// Exporting actions for updating app state
export const {
  settingSelectedDealFile,
  setInitialDealStepValues,
  setFirstStepDetailsOfDeal,
  setSecondStepDetailsOfDeal,
  settingSelectedEmailAndSMSTab,
  settingSelectedDealTab,
  settingViewDetail,
  setAddingDeal,
  setEditDeal,
  settingSelectedClientDeal,
  settingSelectedDeal,
  resettingDealData,
  settingOverviewModal,
  settingArhievedList,
  resettingDealLoader
} = dealSlice.actions;

// Exporting the reducer to be used in the store
export default dealSlice.reducer;
